import { DisplayFile, Uploader, useModal, useUploader } from '@europrocurement/flexy-components';
import {
    GenericModalMailType,
    ModalMailAttachmentsType,
} from '@europrocurement/flexy-components/types/GenericModalMailType';
import { FormStructure } from '@europrocurement/flexy-form';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import IAMenu from '@europrocurement/l2d-modules/modules/IaServices/components/IAMenu/IAMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen } from '@fortawesome/sharp-regular-svg-icons';
import useMailInputsFormStructure from './useMailInputsFormStructure';

type SubmitFooterProps = {
    customButtonsFooter?: React.ReactNode;
    onCancel: () => void;
};

const SubmitFooter: React.FunctionComponent<SubmitFooterProps> = function ({
    customButtonsFooter,
    onCancel,
}) {
    const { formState } = useFormContext();

    if (customButtonsFooter) return customButtonsFooter;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
                color="primary"
                sx={{
                    marginTop: '2rem',
                    marginBottom: '16px',
                    color: 'text.gris70',
                    padding: '16px 24px',
                    fontSize: '16px',
                }}
                onClick={onCancel}
            >
                Annuler
            </Button>
            {formState.isSubmitting ? (
                <Button
                    variant="contained"
                    type="button"
                    sx={{
                        marginTop: '2rem',
                        marginBottom: '16px',
                        padding: '16px 24px',
                        fontSize: '16px',
                    }}
                >
                    <CircularProgress
                        color="inherit"
                        size="16px"
                    />
                </Button>
            ) : (
                <Button
                    variant="contained"
                    type="submit"
                    sx={{
                        marginTop: '2rem',
                        marginBottom: '16px',
                        padding: '16px 24px',
                        fontSize: '16px',
                    }}
                >
                    Envoyer
                </Button>
            )}
        </Box>
    );
};

/**
 * Generate FormStructure based on GenericModalMailContent props
 * Props define defaults values of inputs or/and boolean to display inputs
 *
 * @returns FormStructure[]
 */
const useFormStructureGenericModal = ({
    to,
    cc,
    cci,
    subject,
    body,
    attachments,
    searchParamsTiersIds,
    showTo = false,
    showCC = true,
    showCCI = false,
    showSubject = false,
    showBody = false,
    showIaButtons = false,
    iaChildren,
    showAttachments = false,
    additionnalReactNode,
    customButtonsFooter,
    fieldsOrdering,
    bodyinputSx,
    showDriveDocButton = false,
    onShowDriveDocs,
}: GenericModalMailType & { onShowDriveDocs?: () => void }) => {
    const formStructure = useRef<Array<FormStructure>>([]);
    const { palette } = useTheme();
    const { addFiles } = useUploader();
    const { modalActions } = useModal();
    const {
        createRecipientsInput,
        createNonEditableRecipientsDataInput,
        createNonEditableArrayDataInput,
        createNonEditableSingleDataInput,
    } = useMailInputsFormStructure();

    // Separate effect to handle attachments - only runs when attachments change
    // Prevent dbl re-render and bug file is allready loaded
    useEffect(() => {
        if (showAttachments && attachments && attachments.length > 0) {
            const filesArray: File[] = [];
            attachments.forEach((attachment) => filesArray.push(attachment.file));
            addFiles(filesArray);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachments, showAttachments]);

    // Render default attchment Chip or custom render defined in ModalMailAttachmentsType
    const renderAttachmentFile = (attachment: ModalMailAttachmentsType) => {
        if (attachment.render) {
            return attachment.render;
        }
        return (
            <DisplayFile
                key={`${attachment.file.name}_${attachment.file.type}`}
                nom={attachment.file.name}
                size={attachment.file.size}
                mimetype={attachment.file.type}
                mode="card"
            />
        );
    };

    useEffect(() => {
        formStructure.current = [];

        // Recipients TO
        if (showTo && searchParamsTiersIds && searchParamsTiersIds?.length > 0) {
            formStructure.current = [
                ...formStructure.current,
                createRecipientsInput({
                    searchParamsTiersIds,
                    inputName: 'recipients',
                    inputLabel: 'Envoyer à',
                    listboxTitle: 'Contacts',
                    placeHolder: 'Rechercher un contact',
                }),
            ];
        } else if (to && to.length > 0) {
            formStructure.current = [
                ...formStructure.current,
                createNonEditableRecipientsDataInput({
                    label: 'Envoyer à',
                    name: 'recipientsNonEditable',
                    arrayValues: to,
                }),
            ];
        }

        // RecipientsCC CC
        if (showCC && searchParamsTiersIds && searchParamsTiersIds.length > 0) {
            formStructure.current = [
                ...formStructure.current,
                createRecipientsInput({
                    searchParamsTiersIds,
                    inputName: 'recipientsCC',
                    inputLabel: 'Envoyer en copie à',
                    listboxTitle: 'Contacts',
                    placeHolder: 'Rechercher un contact',
                }),
            ];
        } else if (cc && cc.length > 0) {
            formStructure.current = [
                ...formStructure.current,
                createNonEditableArrayDataInput({
                    label: 'Envoyer en copie à',
                    name: 'recipientsCCNonEditable',
                    arrayValues: cc,
                }),
            ];
        }

        // RecipientsCCI CCI
        if (showCCI && searchParamsTiersIds && searchParamsTiersIds.length > 0) {
            formStructure.current = [
                ...formStructure.current,
                createRecipientsInput({
                    searchParamsTiersIds,
                    inputName: 'recipientsCCI',
                    inputLabel: 'Envoyer en copie cachée à',
                    listboxTitle: 'Contacts',
                    placeHolder: 'Rechercher un contact',
                }),
            ];
        } else if (cci && cci.length > 0) {
            formStructure.current = [
                ...formStructure.current,
                createNonEditableArrayDataInput({
                    label: 'Envoyer en copie cachée à',
                    name: 'recipientsCCINonEditable',
                    arrayValues: cci,
                }),
            ];
        }

        // subject Mail subject
        if (showSubject) {
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'text',
                    name: 'subject',
                    inputlabel: 'Sujet du mail',
                    sx: { minHeight: '100px' },
                },
            ];
        } else if (subject) {
            formStructure.current = [
                ...formStructure.current,
                createNonEditableSingleDataInput({
                    label: 'Sujet',
                    name: 'subjectNonEditable',
                    value: subject,
                }),
            ];
        }

        // IAButtons related to the editable body
        if (showIaButtons && showBody && iaChildren) {
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'customItem',
                    name: 'iaButtons',
                    renderField: () => (
                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            <IAMenu
                                sxProps={{
                                    position: 'absolute',
                                    right: '5px',
                                    top: '18px',
                                }}
                            >
                                {iaChildren}
                            </IAMenu>
                        </Box>
                    ),
                },
            ];
        }

        // mailBody Mail body
        if (showBody) {
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'textarea',
                    name: 'mailBody',
                    inputlabel: 'Corps de mail',
                    sx: {
                        minHeight: '100px',
                        padding: '8px 20px',
                        border: `1px solid ${palette.grey['300']}`,
                        borderRadius: '12px',
                        ':hover': {
                            border: `1px solid ${palette.grey['900']}`,
                        },
                        ':focus-within': {
                            border: `1px solid ${palette.primary.main}`,
                        },
                        transition: 'all ease-in-out 300ms',
                        '&::before': { display: 'none' },
                        '&::after': { display: 'none' },
                        ...bodyinputSx,
                    },
                },
            ];
        } else if (body) {
            formStructure.current = [
                ...formStructure.current,
                createNonEditableSingleDataInput({
                    label: 'Corps de mail',
                    name: 'bodyNonEditable',
                    value: body,
                }),
            ];
        }

        // Drive document button
        if (showDriveDocButton && onShowDriveDocs) {
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'customItem',
                    name: 'driveDocButton',
                    renderField: () => (
                        <Button
                            variant="outlined"
                            startIcon={
                                <FontAwesomeIcon
                                    icon={faFolderOpen}
                                    size="sm"
                                />
                            }
                            onClick={onShowDriveDocs}
                        >
                            Documents du dossier
                        </Button>
                    ),
                },
            ];
        }
        // Attachments
        if (showAttachments) {
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'customItem',
                    name: 'attachmentsUploader',
                    renderField: () => (
                        <Box sx={{ display: 'grid', gap: '4px' }}>
                            <Typography variant="l2dlabel">Pièces-jointes</Typography>
                            <Uploader
                                label="Pièce(s) jointe(s)"
                                sx={{ margin: 0 }}
                                fileUlpoadOverides={{ height: '160px' }}
                            />
                        </Box>
                    ),
                },
            ];
        } else if (attachments && attachments.length > 0) {
            // Display current attachments in the context
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'customItem',
                    name: 'attachmentsItems',
                    renderField: () => (
                        <Box>
                            {attachments.map((attachment) => renderAttachmentFile(attachment))}
                        </Box>
                    ),
                },
            ];
        }

        if (additionnalReactNode) {
            formStructure.current = [
                ...formStructure.current,
                {
                    type: 'customItem',
                    name: 'additionnalReactNode',
                    renderField: () => additionnalReactNode,
                },
            ];
        }

        // footer Buttons
        formStructure.current = [
            ...formStructure.current,
            {
                type: 'customItem',
                name: 'buttonsFooter',
                renderField: () => (
                    <SubmitFooter
                        customButtonsFooter={customButtonsFooter}
                        onCancel={() => modalActions.close()}
                    />
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        additionnalReactNode,
        body,
        cc,
        cci,
        iaChildren,
        subject,
        to,
        showDriveDocButton,
        onShowDriveDocs,
    ]);

    const getFormStructure = () => {
        if (fieldsOrdering && fieldsOrdering.length > 0) {
            // Sort fromStructure by an array of named fields
            formStructure.current.sort((a, b) => {
                let aPositionInArray = fieldsOrdering.indexOf(a.name);
                let bPositionInArray = fieldsOrdering.indexOf(b.name);

                // yeah i know it sounds insane... position in array return the index
                // an index with 0 need to be placed in an higher position than a index with 2
                if (aPositionInArray >= 0 && bPositionInArray >= 0) {
                    if (aPositionInArray > bPositionInArray) {
                        bPositionInArray = 1;
                        aPositionInArray = 0;
                    } else {
                        bPositionInArray = 0;
                        aPositionInArray = 1;
                    }
                }

                return bPositionInArray - aPositionInArray || -1;
            });

            return formStructure.current;
        }
        return formStructure.current;
    };

    return {
        getFormStructure,
    };
};

export default useFormStructureGenericModal;
